import { useEffect, useState } from "react";
import dayjs from "dayjs";

const useBIDashboardState = () => {
    // plot selections
    const [selectPatientPlot, setSelectPatientPlot] = useState<string>("new patients");
    const [selectRxPlot, setSelectRxPlot] = useState<string>("all rx count");
    const [selectScheduleBasedPlot, setSelectScheduleBasedPlot] = useState<string>("total appointment value");
    const [selectRxBlankoPlot, setSelectRxBlankoPlot] = useState<string>("all rx blanko count");

    // delta type selections
    const [selectRxDeltaType, setSelectRxDeltaType] = useState<'rxDateToBooking' | 'firstTreatmentToBooking' | 'lastTreatmentToBooking'>('rxDateToBooking');
    const [selectRxBlankoDeltaType, setSelectRxBlankoDeltaType] = useState<'rxDateToBooking' | 'firstTreatmentToBooking' | 'lastTreatmentToBooking'>('rxDateToBooking');
    
    // filter to exclude rx blanko
    const [rxBlankoFilter, setRxBlankoFilter] = useState<boolean>(false);

    // bin type selections
    const [patientBinType, setPatientBinType] = useState<"day" | "week" | "month" | "year">("day");
    const [rxBinType, setRxBinType] = useState<"day" | "week" | "month" | "year">("day");
    const [scheduleBinType, setScheduleBinType] = useState<"day" | "week" | "month" | "year">("day");
    const [rxBlankoBinType, setRxBlankoBinType] = useState<"day" | "week" | "month" | "year">("day");

    // date ranges - default to current month
    const today = new Date();
    const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    const endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0, 0, 0, 0, 0));
    // console.log(`current month: ${startOfMonth} to ${endOfMonth}`);

    const [patientStartDate, setPatientStartDate] = useState<Date>(startOfMonth);
    const [patientEndDate, setPatientEndDate] = useState<Date>(endOfMonth);
    const [rxStartDate, setRxStartDate] = useState<Date>(startOfMonth);
    const [rxEndDate, setRxEndDate] = useState<Date>(endOfMonth);
    const [scheduleStartDate, setScheduleStartDate] = useState<Date>(startOfMonth);
    const [scheduleEndDate, setScheduleEndDate] = useState<Date>(endOfMonth);
    const [therapistStartDate, setTherapistStartDate] = useState<Date>(startOfMonth);
    const [therapistEndDate, setTherapistEndDate] = useState<Date>(endOfMonth);
    const [revenueMonth, setRevenueMonth] = useState<Date>(startOfMonth);
    const [rxBlankoStartDate, setRxBlankoStartDate] = useState<Date>(startOfMonth);
    const [rxBlankoEndDate, setRxBlankoEndDate] = useState<Date>(endOfMonth);
    const [yearEndRxUnfinishedValuesStartDate, setYearEndRxUnfinishedValuesStartDate] = useState<Date>(dayjs().startOf('year').toDate());
    const [yearEndRxUnfinishedValuesEndDate, setYearEndRxUnfinishedValuesEndDate] = useState<Date>(dayjs().endOf('year').toDate());
    // console.log("revenue month start", revenueMonth);

    // table selections
    const [patientTable, setPatientTable] = useState<"Zusammenfassung" | "Alle Daten">("Alle Daten");
    const [rxTable, setRxTable] = useState<"Zusammenfassung" | "Alle Daten">("Alle Daten");
    const [scheduleTable, setScheduleTable] = useState<"Zusammenfassung" | "Alle Daten">("Alle Daten");
    const [monthlyRevenueTable, setMonthlyRevenueTable] = useState<"Zusammenfassung" | "Alle Daten">("Alle Daten");
    const [rxBlankoTable, setRxBlankoTable] = useState<"Zusammenfassung" | "Alle Daten">("Alle Daten");

    // view selections
    const [patientView, setPatientView] = useState<"plot" | "table">("plot");
    const [rxView, setRxView] = useState<"plot" | "table">("plot");
    const [scheduleView, setScheduleView] = useState<"plot" | "table">("plot");
    const [monthlyRevenueView, setMonthlyRevenueView] = useState<"plot" | "table">("plot");
    const [rxBlankoView, setRxBlankoView] = useState<"plot" | "table">("plot");

    // location selection - one state per plot variant
    // Patient plots: "new patients", "cumulative patients"
    const [patientLocations_newPatients, setPatientLocations_newPatients] = useState<string[]>([]);
    const [patientLocations_cumulativePatients, setPatientLocations_cumulativePatients] = useState<string[]>([]);

    // Rx plots: "all rx count", "all rx value", "rx therapist count", "rx lifespan distribution"
    const [rxLocations_allRxCount, setRxLocations_allRxCount] = useState<string[]>([]);
    const [rxLocations_allRxValue, setRxLocations_allRxValue] = useState<string[]>([]);
    const [rxLocations_rxTherapistCount, setRxLocations_rxTherapistCount] = useState<string[]>([]);
    const [rxLocations_rxLifespanDistribution, setRxLocations_rxLifespanDistribution] = useState<string[]>([]);

    // Schedule plots: "total appointment value", "all rx count", "total gaps", "therapyRx frequency"
    const [scheduleLocations_totalAppointmentValue, setScheduleLocations_totalAppointmentValue] = useState<string[]>([]);
    const [scheduleLocations_allRxCount, setScheduleLocations_allRxCount] = useState<string[]>([]);
    const [scheduleLocations_totalGaps, setScheduleLocations_totalGaps] = useState<string[]>([]);
    const [scheduleLocations_therapyRxFrequency, setScheduleLocations_therapyRxFrequency] = useState<string[]>([]);

    // Monthly revenue (single plot)
    const [monthlyRevenueLocations, setMonthlyRevenueLocations] = useState<string[]>([]);

    // Rx Blanko plots: "all rx blanko count", "all rx blanko value", "rx blanko therapist count", "rx blanko lifespan distribution"
    const [rxBlankoLocations_allRxBlankoCount, setRxBlankoLocations_allRxBlankoCount] = useState<string[]>([]);
    const [rxBlankoLocations_allRxBlankoValue, setRxBlankoLocations_allRxBlankoValue] = useState<string[]>([]);
    const [rxBlankoLocations_rxBlankoTherapistCount, setRxBlankoLocations_rxBlankoTherapistCount] = useState<string[]>([]);
    const [rxBlankoLocations_rxBlankoLifespanDistribution, setRxBlankoLocations_rxBlankoLifespanDistribution] = useState<string[]>([]);

    // Therapist count filter for rx therapist count plots
    const [therapistCountFilter, setTherapistCountFilter] = useState<number[] | 'all'>('all');

    return {
        selectPatientPlot, setSelectPatientPlot,
        selectRxPlot, setSelectRxPlot,
        selectScheduleBasedPlot, setSelectScheduleBasedPlot,
        selectRxBlankoPlot, setSelectRxBlankoPlot,
        
        selectRxDeltaType, setSelectRxDeltaType,
        selectRxBlankoDeltaType, setSelectRxBlankoDeltaType,

        rxBlankoFilter, setRxBlankoFilter,

        patientBinType, setPatientBinType,
        rxBinType, setRxBinType,
        scheduleBinType, setScheduleBinType,
        rxBlankoBinType, setRxBlankoBinType,

        patientStartDate, setPatientStartDate,
        patientEndDate, setPatientEndDate,
        rxStartDate, setRxStartDate,
        rxEndDate, setRxEndDate,
        scheduleStartDate, setScheduleStartDate,
        scheduleEndDate, setScheduleEndDate,
        therapistStartDate, setTherapistStartDate,
        therapistEndDate, setTherapistEndDate,
        revenueMonth, setRevenueMonth,
        rxBlankoStartDate, setRxBlankoStartDate,
        rxBlankoEndDate, setRxBlankoEndDate,
        yearEndRxUnfinishedValuesStartDate, setYearEndRxUnfinishedValuesStartDate,
        yearEndRxUnfinishedValuesEndDate, setYearEndRxUnfinishedValuesEndDate,

        patientTable, setPatientTable,
        rxTable, setRxTable,
        scheduleTable, setScheduleTable,
        monthlyRevenueTable, setMonthlyRevenueTable,
        rxBlankoTable, setRxBlankoTable,
        
        patientView, setPatientView,
        rxView, setRxView,
        scheduleView, setScheduleView,
        monthlyRevenueView, setMonthlyRevenueView,
        rxBlankoView, setRxBlankoView,

        // patient locations
        patientLocations_newPatients, setPatientLocations_newPatients,
        patientLocations_cumulativePatients, setPatientLocations_cumulativePatients,

        // rx locations
        rxLocations_allRxCount, setRxLocations_allRxCount,
        rxLocations_allRxValue, setRxLocations_allRxValue,
        rxLocations_rxTherapistCount, setRxLocations_rxTherapistCount,
        rxLocations_rxLifespanDistribution, setRxLocations_rxLifespanDistribution,

        // schedule locations
        scheduleLocations_totalAppointmentValue, setScheduleLocations_totalAppointmentValue,
        scheduleLocations_allRxCount, setScheduleLocations_allRxCount,
        scheduleLocations_totalGaps, setScheduleLocations_totalGaps,
        scheduleLocations_therapyRxFrequency, setScheduleLocations_therapyRxFrequency,

        // monthly revenue locations
        monthlyRevenueLocations, setMonthlyRevenueLocations,

        // rx blanko locations
        rxBlankoLocations_allRxBlankoCount, setRxBlankoLocations_allRxBlankoCount,
        rxBlankoLocations_allRxBlankoValue, setRxBlankoLocations_allRxBlankoValue,
        rxBlankoLocations_rxBlankoTherapistCount, setRxBlankoLocations_rxBlankoTherapistCount,
        rxBlankoLocations_rxBlankoLifespanDistribution, setRxBlankoLocations_rxBlankoLifespanDistribution,

        // therapist count filter
        therapistCountFilter, setTherapistCountFilter
    }
};

export default useBIDashboardState;